<template>
  <!--
    MK3 sidebar — dark left rail with primary nav and three collapsible
    grouped sections: Reports, Data Management, Admin. Sourced from
    /Users/simoncrunden/travel-analytics's layouts/SideBar.vue (MK3
    visual reference) and adapted to:
      - the analytics-mk2 menu shape (`getMenu(user)` with `url`,
        `label`, `icon`, `children`)
      - Tailwind `tw-` prefix
      - FontAwesome icons (decision: keep FA across MK2 → MK3 cutover)

    MK2 menu top-level groups → MK3 sidebar sections:
      `/view`     — Dashboards          → flattened into primary nav
      `/feature`  — Reports             → "Reports" section
      `/editing`  — Data Editing        → split:
                       Travel Policies, Travellers, Service Fee Types
                       → "Data Management" section
                       Bookings (editor) → ADMIN section
      `/config`   — Admin               → ADMIN section
    Permission gates (handled in menu.ts) prune empty children before
    they reach this component, so each section only renders if it has
    visible items.
  -->
  <aside
    :class="[
      'tw-bg-gray-900 tw-text-white tw-transition-all tw-duration-300 tw-ease-in-out',
      'tw-fixed lg:tw-relative tw-inset-y-0 tw-left-0 tw-z-40',
      'tw-flex tw-flex-col',
      uiStore.isSidebarCollapsed ? 'tw-w-16' : 'tw-w-64',
    ]"
  >
    <!-- Brand — shows the active TMC's name (same value as the
         "Travel Agent Name" in the TMC Details settings tab,
         settings/tabs/TmcDetails.vue). Falls back to "Travel
         Analytics" if no active client is set, so logged-out /
         pre-bootstrap states still render something.

         Sidebar collapse/expand is self-controlled here:
           - Expanded:  full brand name + a left-chevron collapse
                        button on the right.
           - Collapsed: just the initials, which act as the expand
                        button. Clicking anywhere on the rail's
                        brand area toggles back open. -->
    <div class="tw-flex tw-items-center tw-justify-between tw-h-16 tw-px-4">
      <template v-if="!uiStore.isSidebarCollapsed">
        <div class="tw-flex tw-items-center tw-min-w-0 tw-flex-1">
          <span class="tw-text-xl tw-font-bold tw-truncate">{{ brandName }}</span>
        </div>
        <button
          type="button"
          class="tw-ml-2 tw-p-2 tw-rounded-lg tw-text-gray-300 hover:tw-text-white hover:tw-bg-gray-800 tw-border-0 tw-bg-transparent tw-cursor-pointer"
          aria-label="Collapse sidebar"
          @click="uiStore.toggleSidebar"
        >
          <i class="fa fa-chevron-left" />
        </button>
      </template>
      <button
        v-else
        type="button"
        class="tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full tw-text-xl tw-font-bold tw-text-white tw-border-0 tw-bg-transparent tw-cursor-pointer hover:tw-bg-gray-800 tw-transition-colors"
        aria-label="Expand sidebar"
        @click="uiStore.toggleSidebar"
      >
        {{ brandInitials }}
      </button>
    </div>

    <!-- Nav -->
    <nav class="tw-flex-1 tw-overflow-y-auto tw-py-4">
      <ul class="tw-space-y-1 tw-px-2">
        <!-- Primary items (flattened from /view group) -->
        <li v-for="item in primaryItems" :key="item.url">
          <router-link
            :to="item.url"
            :class="navLinkClasses(item.url)"
          >
            <i
              v-if="item.icon"
              :class="[item.icon, 'tw-text-lg', 'tw-w-5', 'tw-text-center']"
            />
            <span
              v-if="!uiStore.isSidebarCollapsed"
              class="tw-ml-3 tw-text-sm tw-font-medium"
            >{{ item.label }}</span>
          </router-link>
        </li>

        <!-- Grouped sections (Reports, Data Management, Admin).
             Each is collapsible when sidebar is expanded; in
             collapsed mode the items render as a flat icon strip
             below a thin divider. -->
        <template
          v-for="section in sections"
          :key="section.key"
        >
          <template v-if="section.items.length">
            <!-- Section header (expanded). Explicit bg-gray-900 to
                 match the sidebar; tw-border-0 kills the browser
                 default <button> border (Tailwind preflight is off
                 in MK2 so resets don't apply). The blue pill is the
                 only visual cue per section. tw-gap-2 between the
                 label group and the chevron prevents the chevron
                 from kissing a long pill on narrow widths. -->
            <li v-if="!uiStore.isSidebarCollapsed" class="tw-pt-4">
              <button
                type="button"
                class="tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-py-2 tw-bg-gray-900 tw-text-gray-400 tw-border-0 tw-cursor-pointer"
                @click="toggleSection(section)"
              >
                <div class="tw-flex tw-items-center tw-gap-2">
                  <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider">
                    {{ section.label }}
                  </span>
                  <span
                    v-if="section.badge"
                    class="tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-blue-600 tw-text-white tw-rounded-full"
                  >
                    {{ section.badge }}
                  </span>
                </div>
                <i :class="isExpanded(section) ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" />
              </button>
            </li>
            <!-- Divider (collapsed) -->
            <li v-else class="tw-pt-4">
              <div class="tw-border-t tw-border-gray-700" />
            </li>

            <!-- Items (expanded + section open) -->
            <template v-if="!uiStore.isSidebarCollapsed && isExpanded(section)">
              <li v-for="item in section.items" :key="item.url">
                <router-link
                  :to="item.url"
                  :class="navLinkClasses(item.url)"
                >
                  <i
                    v-if="item.icon"
                    :class="[item.icon, 'tw-text-lg', 'tw-w-5', 'tw-text-center']"
                  />
                  <span class="tw-ml-3 tw-text-sm tw-font-medium">{{ item.label }}</span>
                </router-link>
              </li>
            </template>

            <!-- Items (collapsed sidebar — show icons regardless) -->
            <template v-else-if="uiStore.isSidebarCollapsed">
              <li v-for="item in section.items" :key="item.url">
                <router-link
                  :to="item.url"
                  :class="navLinkClasses(item.url)"
                >
                  <i
                    v-if="item.icon"
                    :class="[item.icon, 'tw-text-lg', 'tw-w-5', 'tw-text-center']"
                  />
                </router-link>
              </li>
            </template>
          </template>
        </template>

        <!-- Help — standalone link at the foot of the nav (not a
             collapsible section). Always visible; the TMC-only Admin
             Guide is a tab inside the page, not a separate route. -->
        <li
          v-if="helpItem"
          class="tw-pt-4"
        >
          <router-link
            :to="helpItem.url"
            :class="navLinkClasses(helpItem.url)"
          >
            <i
              v-if="helpItem.icon"
              :class="[helpItem.icon, 'tw-text-lg', 'tw-w-5', 'tw-text-center']"
            />
            <span
              v-if="!uiStore.isSidebarCollapsed"
              class="tw-ml-3 tw-text-sm tw-font-medium"
            >{{ helpItem.label }}</span>
          </router-link>
        </li>
      </ul>
    </nav>

    <!-- Client logo. Carried over from MK2 (src/components/ClientLogo.vue
         pulls the active client's logo from the TMC settings or
         falls back to logo_key). Hidden when the sidebar is
         collapsed — 64px width has no room for a logo, and the
         scaled-down version reads as visual clutter. -->
    <div
      v-if="!uiStore.isSidebarCollapsed"
      class="tw-p-4 tw-flex tw-items-center tw-justify-center"
    >
      <ClientLogo />
    </div>
  </aside>
</template>

<script setup lang="ts">
  import { reactive, computed, watch, ref } from 'vue'
  import { useRoute } from 'vue-router'
  import { storeToRefs } from 'pinia'

  import { useAccountsStore } from '@/stores/accounts'
  import { useTreeStore } from '@/stores/tree'
  import { useUiStore } from '@/stores/ui'
  import { getMenu } from '@/menu'
  import ClientLogo from '@/components/ClientLogo.vue'

  defineOptions({ name: 'Mk3Sidebar' })

  const route = useRoute()
  const uiStore = useUiStore()
  const { user } = storeToRefs(useAccountsStore())
  const { clientsGetter: clients } = storeToRefs(useTreeStore())

  // Brand label sourced from the active client (TMC) record — same
  // value rendered as "Travel Agent Name" in the TMC Details
  // settings tab. Falls back to "Travel Analytics" when no client
  // is active (pre-login or pre-bootstrap renders).
  const brandName = computed(() => {
    const activeId = (user.value as any)?.active_client
    if (!activeId) return 'Travel Analytics'
    const client = clients.value?.[activeId]
    // Prefer the TMC's display-name alias (TAMK3-25) over the raw name.
    return client?.settings?.name_alias || client?.name || 'Travel Analytics'
  })

  // Collapsed-state initials — first letter of each word, max 2 chars.
  // "Goldman Travel" → "GT", "Travel Analytics" → "TA", single-word
  // names fall back to the first two letters.
  const brandInitials = computed(() => {
    const name = brandName.value
    const parts = name.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  })

  type MenuItem = { label: string; url: string; icon?: string; children?: MenuItem[] }

  // Group identity by URL — the parents `getMenu(user)` produces
  // top-level items for. Keeping these as constants (vs deriving by
  // label) makes the code resilient to label edits in menu.ts.
  //
  // Pre-Admin-restructure (May 2026) there was a separate `/editing`
  // top-level group housing Travel Policies / Traveller Merging /
  // Service Fee Type Merging / Booking Editing, and the sidebar
  // split them into "Data Management" + "Admin" sections. Post-
  // restructure all admin functionality lives under `/config` with
  // Data Management now a sub-tab there, so the sidebar is back to
  // a single Admin section.
  const VIEW_GROUP_URL = '/view'
  const REPORTS_GROUP_URL = '/feature'
  const SUPPLIERS_GROUP_URL = '/preferred-suppliers'
  const CONFIG_GROUP_URL = '/config'
  const HELP_GROUP_URL = '/help'

  const navItems = ref<MenuItem[]>([])
  watch(user, (u) => {
    navItems.value = (getMenu(u) || []) as MenuItem[]
  }, { immediate: true, deep: true })

  function findGroup (url: string): MenuItem | undefined {
    return navItems.value.find(g => g.url === url)
  }

  // Visible-by-label filter — the menu's `itemToMenu` returns
  // undefined for items without a label and `.filter(Boolean)` drops
  // them, but defensively re-filter here in case the menu shape
  // changes.
  function visible (items: MenuItem[] | undefined): MenuItem[] {
    return (items || []).filter(c => !!c.label)
  }

  const primaryItems = computed(() => visible(findGroup(VIEW_GROUP_URL)?.children))
  const reportsItems = computed(() => visible(findGroup(REPORTS_GROUP_URL)?.children))
  const suppliersItems = computed(() => visible(findGroup(SUPPLIERS_GROUP_URL)?.children))
  const adminItems = computed(() => visible(findGroup(CONFIG_GROUP_URL)?.children))
  // Help is a single top-level route (no children) — findGroup returns
  // the item itself, rendered as a standalone link below the sections.
  const helpItem = computed(() => findGroup(HELP_GROUP_URL))

  type Section = {
    key: 'reports' | 'suppliers' | 'admin'
    label: string
    badge?: string
    items: MenuItem[]
  }

  // Section badges sit on each header button as small blue pills,
  // providing colour contrast against the gray-800 button chrome
  // and drawing the eye to the section divider. DOWNLOAD on
  // Reports signals "downloadable artefacts"; ADMIN on Admin
  // signals admin-permission gating.
  const sections = computed<Section[]>(() => [
    { key: 'reports', label: 'Reports', badge: 'DOWNLOAD', items: reportsItems.value },
    { key: 'suppliers', label: 'Preferred Suppliers', items: suppliersItems.value },
    { key: 'admin', label: 'Admin', badge: 'ADMIN', items: adminItems.value },
  ])

  // Section expand/collapse — context-aware. A section is auto-open
  // when the active route is one of its items, auto-closed otherwise.
  // User clicks (toggle) override the auto behaviour but the override
  // is cleared on every route change so the auto rule kicks in again.
  // Net effect: sections open themselves when you're using them,
  // collapse when you're not, but you can always force a peek.
  const manualToggles = reactive<Record<Section['key'], boolean | null>>({
    reports: null,
    suppliers: null,
    admin: null,
  })

  watch(() => route.path, () => {
    manualToggles.reports = null
    manualToggles.suppliers = null
    manualToggles.admin = null
  })

  function sectionContainsActiveRoute (section: Section): boolean {
    return section.items.some(item => isActive(item.url))
  }

  function isExpanded (section: Section): boolean {
    const override = manualToggles[section.key]
    if (override !== null) return override
    return sectionContainsActiveRoute(section)
  }

  function toggleSection (section: Section) {
    manualToggles[section.key] = !isExpanded(section)
  }

  function isActive (path: string): boolean {
    return route.path === path || route.path.startsWith(path + '/')
  }

  function navLinkClasses (path: string) {
    const active = isActive(path)
    return [
      // tw-no-underline kills the browser's default <a> underline that
      // bleeds through router-link.
      'tw-flex tw-items-center tw-px-3 tw-py-2.5 tw-rounded-lg tw-transition-colors tw-duration-200 tw-no-underline',
      'hover:tw-bg-gray-800',
      active
        ? 'tw-bg-blue-600 tw-text-white'
        : 'tw-text-gray-300 hover:tw-text-white',
    ]
  }
</script>

<style scoped>
  /* Custom scrollbar — matches MK3 reference. Slightly more visible on
     dark sidebar than browser default so users notice it's scrollable. */
  nav::-webkit-scrollbar {
    width: 6px;
  }
  nav::-webkit-scrollbar-track {
    background: transparent;
  }
  nav::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
  nav::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
</style>
